/**
 * Resolvers for organization scheduling config (quiet hours, active hours, and
 * quiet-hours settings) that bridge the two FE sources: prefer the React Query
 * organization cache (authoritative after fetch), then fall back to the Redux
 * `organizations` slice for legacy paths. Pulled out of `quiet-hours.ts` so
 * non-QH features (e.g. scheduled-message timezone resolution) can resolve the
 * org active-hours fallback without importing the quiet-hours module.
 */

import { QUERY_KEYS } from '@app/constants/service';
import type { ReadableQueryClient } from '@app/query-client';
import type {
  ActiveHoursConfig,
  Organization,
  QuietHoursSettings,
} from '@app/types/api/organization';

/**
 * Resolves org active-hours config: prefer the React Query organization cache
 * (authoritative after fetch), then fall back to Redux
 * `state.organizations.activeHours`. Used as the scheduled-message org fallback
 * in `resolveCaseTimeZone` and as the active-hours source for
 * `resolveEffectiveQuietHoursConfig`.
 */
export const resolveOrganizationActiveHours = ({
  organizationId,
  queryClient,
  reduxActiveHours,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxActiveHours?: ActiveHoursConfig;
}): ActiveHoursConfig | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.activeHours !== undefined) {
      return cached.activeHours;
    }
  }
  return reduxActiveHours;
};

/**
 * Resolves org quiet-hours settings (organizationSettings.quietHours) the same
 * way active hours are resolved: prefer the React Query organization cache, then
 * fall back to Redux `state.organizations.organizationSettings.quietHours`.
 */
export const resolveOrganizationQuietHoursSettings = ({
  organizationId,
  queryClient,
  reduxQuietHoursSettings,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxQuietHoursSettings?: QuietHoursSettings;
}): QuietHoursSettings | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.organizationSettings?.quietHours !== undefined) {
      return cached.organizationSettings.quietHours;
    }
  }
  return reduxQuietHoursSettings;
};
